import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Save, Phone, Building2, User as UserIcon, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RatingStars } from "@/components/RatingStars";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoneInput } from "@/components/PhoneInput";
import { parsePhone, isValidPhone } from "@/lib/constants";

export default function Profile() {
  const { auth, checkAuth } = useAuth();
  const u = auth?.user;
  const [form, setForm] = useState({ name: "", phone: "", company: "", contact_email: "" });
  const [saving, setSaving] = useState(false);
  const [rating, setRating] = useState({ average: 0, count: 0 });

  useEffect(() => {
    if (u) {
      setForm({ name: u.name || "", phone: u.phone || "", company: u.company || "", contact_email: u.contact_email || u.email || "" });
      api.get(`/ratings/${u.user_id}`).then((r) => setRating(r.data)).catch(() => {});
    }
  }, [u]);

  const save = async () => {
    if (parsePhone(form.phone).local.length !== 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    if (form.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) { toast.error("Enter a valid contact email address."); return; }
    setSaving(true);
    try {
      await api.put("/auth/profile", form);
      await checkAuth();
      toast.success("Profile updated");
    } catch (e) { toast.error("Could not save profile"); }
    finally { setSaving(false); }
  };

  const phoneMissing = !u?.phone;
  const phoneInvalid = !!u?.phone && !isValidPhone(u.phone);

  return (
    <div className="max-w-xl">
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-1">Profile</h1>
      <p className="text-muted-foreground text-sm mb-6">Your details appear on the loads and trucks you post.</p>

      {(phoneMissing || phoneInvalid) && (
        <div data-testid="profile-phone-warning" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 mb-6 text-sm">
          {phoneInvalid
            ? "Your saved contact number looks incorrect. Please re-enter a valid 10-digit number so the WhatsApp button works on your postings."
            : "Add your phone number to enable the WhatsApp button on your postings."}
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card/60 p-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="w-16 h-16 border border-border"><AvatarImage src={u?.picture} /><AvatarFallback className="bg-secondary">{u?.name?.[0] || "U"}</AvatarFallback></Avatar>
          <div>
            <p className="font-heading font-semibold text-lg">{u?.name}</p>
            <p className="text-sm text-muted-foreground">{u?.email}</p>
            <div className="mt-1.5 flex items-center gap-2" data-testid="profile-trust-score">
              <span className="text-xs uppercase tracking-widest text-primary font-semibold">Trust score</span>
              <RatingStars value={rating.average} count={rating.count} size={13} />
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><UserIcon className="w-3.5 h-3.5" /> Contact name</Label>
            <Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> Phone / WhatsApp number</Label>
            <PhoneInput testid="profile-phone-input" value={form.phone} onChange={(v) => setForm(f => ({ ...f, phone: v }))} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> Contact email</Label>
            <Input data-testid="profile-contact-email-input" type="email" value={form.contact_email} onChange={(e) => setForm(f => ({ ...f, contact_email: e.target.value }))} placeholder="e.g. dispatch@yourcompany.com" />
            <p className="text-xs text-muted-foreground">Signed in as {u?.email} — this contact email is what others see and can differ from your login.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Transport / Company name</Label>
            <Input data-testid="profile-company-input" value={form.company} onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Your company name" />
          </div>
          <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save profile"}
          </Button>
        </div>
      </div>
    </div>
  );
}
